import React, { useState, useEffect } from 'react';
import { X, Package, Calendar, DollarSign, Shield, Plus, Truck } from 'lucide-react';
import { componentService } from '../../services/componentService';
import { supplierService } from '../../services/supplierService';

const ComponentModal = ({ assetId, component, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [showNewSupplierForm, setShowNewSupplierForm] = useState(false);
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    code: '',
    contactPerson: '',
    email: '',
    phone: ''
  });
  const [savingSupplier, setSavingSupplier] = useState(false);

  const [formData, setFormData] = useState({
    templateId: '',
    name: '',
    partNumber: '',
    serialNumber: '',
    supplierId: '',
    installationDate: '',
    replacementCycleMonths: '',
    alertDaysBefore: '30',
    lastReplacementCost: '',
    warrantyMonths: '',
    warrantyExpiration: '',
    warrantyProvider: '',
    notes: '',
    registerAsAsset: false
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (component) {
      setFormData({
        templateId: component.templateId || '',
        name: component.name || '',
        partNumber: component.partNumber || '',
        serialNumber: component.serialNumber || '',
        supplierId: component.supplierId || '',
        installationDate: component.installationDate || '',
        replacementCycleMonths: component.replacementCycleMonths || '',
        alertDaysBefore: component.alertDaysBefore || '30',
        lastReplacementCost: component.lastReplacementCost || '',
        warrantyMonths: component.warrantyMonths || '',
        warrantyExpiration: component.warrantyExpiration || '',
        warrantyProvider: component.warrantyProvider || '',
        notes: component.notes || ''
      });
    }
  }, [component]);

  const loadInitialData = async () => {
    setDataLoading(true);
    try {
      // Load templates
      let templatesData = [];
      try {
        templatesData = await componentService.getTemplates();
      } catch (err) {
        console.error('Error loading templates:', err);
      }

      // Load suppliers
      let suppliersData = [];
      try {
        suppliersData = await supplierService.getSuppliers();
        console.log('Loaded suppliers:', suppliersData);
      } catch (err) {
        console.error('Error loading suppliers:', err);
      }

      setTemplates(templatesData || []);
      setSuppliers(suppliersData || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    setFormData(prev => ({ ...prev, templateId }));

    if (templateId) {
      const template = templates.find(t => t.id === parseInt(templateId));
      if (template) {
        setFormData(prev => ({
          ...prev,
          name: template.name,
          replacementCycleMonths: template.defaultReplacementCycleMonths || '',
          alertDaysBefore: template.defaultAlertDays || '30'
        }));
      }
    }
  };

  const handleSupplierChange = (value) => {
    if (value === 'new') {
      setShowNewSupplierForm(true);
      setFormData(prev => ({ ...prev, supplierId: '' }));
    } else {
      setFormData(prev => ({ ...prev, supplierId: value }));
    }
  };

  const handleCreateSupplier = async () => {
    if (!newSupplier.name.trim()) {
      alert('Supplier name is required');
      return;
    }

    setSavingSupplier(true);
    try {
      // Generate a code if not provided
      const supplierData = {
        ...newSupplier,
        code: newSupplier.code || `SUP-${Date.now().toString().slice(-6)}`,
        supplierType: 'PARTS',
        status: 'ACTIVE'
      };

      const created = await supplierService.createSupplier(supplierData);

      // Add to suppliers list and select it
      setSuppliers(prev => [...prev, created]);
      setFormData(prev => ({ ...prev, supplierId: created.id }));

      // Reset and close new supplier form
      setNewSupplier({ name: '', code: '', contactPerson: '', email: '', phone: '' });
      setShowNewSupplierForm(false);
    } catch (error) {
      console.error('Error creating supplier:', error);
      alert('Error creating supplier. Please try again.');
    } finally {
      setSavingSupplier(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (component) {
        await componentService.updateComponent(component.id, formData);
      } else {
        await componentService.createComponent(assetId, formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving component:', error);
      alert('Error saving component. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {component ? 'Edit Component' : 'Add Component'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {dataLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
            <p className="text-gray-500">Loading...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Template Selection */}
            <div className="bg-blue-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-blue-900 mb-2">
                Component Template (Optional)
              </label>
              <select
                value={formData.templateId}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">Custom Component</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-blue-600 mt-1">
                Select a template to auto-fill default values
              </p>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="e.g., Battery"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Part Number</label>
                <input
                  type="text"
                  value={formData.partNumber}
                  onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., BAT-12V-100AH"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                <input
                  type="text"
                  value={formData.serialNumber}
                  onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., SN123456"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                <select
                  value={formData.supplierId}
                  onChange={(e) => handleSupplierChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                  <option value="new">+ Add New Supplier</option>
                </select>
              </div>
            </div>

            {/* New Supplier Form */}
            {showNewSupplierForm && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="w-5 h-5 text-green-600" />
                  <h4 className="font-medium text-green-900">Add New Supplier</h4>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={newSupplier.name}
                      onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                      placeholder="Supplier name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
                    <input
                      type="text"
                      value={newSupplier.code}
                      onChange={(e) => setNewSupplier({ ...newSupplier, code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                      placeholder="Auto-generated if empty"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Contact Person</label>
                    <input
                      type="text"
                      value={newSupplier.contactPerson}
                      onChange={(e) => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                      placeholder="Contact name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={newSupplier.email}
                      onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={newSupplier.phone}
                      onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                      placeholder="Phone number"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewSupplierForm(false);
                      setNewSupplier({ name: '', code: '', contactPerson: '', email: '', phone: '' });
                    }}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateSupplier}
                    disabled={savingSupplier}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    {savingSupplier ? 'Saving...' : 'Create Supplier'}
                  </button>
                </div>
              </div>
            )}

            {/* Replacement Schedule */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Replacement Schedule
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cycle (months) *</label>
                  <input
                    type="number"
                    value={formData.replacementCycleMonths}
                    onChange={(e) => setFormData({ ...formData, replacementCycleMonths: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    min="1"
                    placeholder="e.g., 24"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Installation Date</label>
                  <input
                    type="date"
                    value={formData.installationDate}
                    onChange={(e) => setFormData({ ...formData, installationDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alert Days Before *</label>
                  <input
                    type="number"
                    value={formData.alertDaysBefore}
                    onChange={(e) => setFormData({ ...formData, alertDaysBefore: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    min="1"
                    placeholder="e.g., 30"
                  />
                </div>
              </div>
            </div>

            {/* Cost */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Cost
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Replacement Cost</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.lastReplacementCost}
                  onChange={(e) => setFormData({ ...formData, lastReplacementCost: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 250.00"
                />
              </div>
            </div>

            {/* Warranty */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Warranty (Optional)
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Warranty Period (months)</label>
                  <input
                    type="number"
                    value={formData.warrantyMonths}
                    onChange={(e) => setFormData({ ...formData, warrantyMonths: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="0"
                    placeholder="e.g., 12"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Warranty Expiration</label>
                  <input
                    type="date"
                    value={formData.warrantyExpiration}
                    onChange={(e) => setFormData({ ...formData, warrantyExpiration: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Warranty Provider</label>
                  <input
                    type="text"
                    value={formData.warrantyProvider}
                    onChange={(e) => setFormData({ ...formData, warrantyProvider: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Manufacturer"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Additional notes about this component..."
              />
            </div>

            {/* Register as Asset toggle — only on create */}
            {!component && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-amber-900 text-sm">Register as a managed asset</div>
                  <div className="text-amber-700 text-xs mt-0.5">
                    Creates a linked Asset record so this component appears in the Assets list with this asset as its parent.
                    Useful for significant parts (batteries, motors, pumps).
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, registerAsAsset: !prev.registerAsAsset }))}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    formData.registerAsAsset ? 'bg-amber-500' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                    formData.registerAsAsset ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : (component ? 'Update Component' : 'Add Component')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ComponentModal;
